import React, { useRef } from "react";
import "./searchfilterProduct.css";
import { useEffect } from "react";
import api from "../../../seller-apis/api";
import { useState } from "react";

/**
 * This component is used on Brand Page, Apply For Brand Page
 * Enhanced with partial search, auto-correction, and result count
 */
function ProductSKUSearch(props) {
  let timer = null;
  const [fuzzySearches, setFuzzySearches] = useState([]);
  const [searchType, setSearchType] = useState("Product Name");
  const [newEnqName, setnewEnqName] = useState("");
  const [value, setValue] = useState("");
  const [resultCount, setResultCount] = useState(0);
  const [showResultCount, setShowResultCount] = useState(false);
  const [correctedQuery, setCorrectedQuery] = useState("");
  const searchContainerRef = useRef(null);
  const skipFuzzySearch = useRef(false);
  const isResetting = useRef(false);
  const firstRender = useRef(true);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [hsnFilter, setHsnFilter] = useState("All");

  // Reset search type when resetSearchType prop changes
  useEffect(() => {
    if (props.resetSearchType > 0) {
      isResetting.current = true;
      setSearchType("Product Name");
      setValue("");
      setMinPrice("");
      setMaxPrice("");
      setHsnFilter("All");
      setFuzzySearches([]);
      setResultCount(0);
      setShowResultCount(false);
      setCorrectedQuery("");
      setTimeout(() => {
        isResetting.current = false;
      }, 100);
    }
  }, [props.resetSearchType]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    // **FIX: Clear dropdown when value is empty**
    if (value === "" || value.trim() === "") {
      setFuzzySearches([]);
      setResultCount(0);
      setShowResultCount(false);
      setCorrectedQuery("");
      
      if (
        (props?.flag === "Opportunity" ||
          props?.flag === "Product" ||
          props?.flag === "Order") &&
        !isResetting.current
      ) {
        props?.apiCall();
      }
      return; // Exit early, don't trigger fuzzy search
    }

    // Only trigger fuzzy search if value has actual content
    if (value.trim() !== "") {
      let timeoutId = setTimeout(() => {}, 500);
      timeoutId = setTimeout(() => fuzzySearch(), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setFuzzySearches([]);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Only trigger fuzzy search if there's actual content in any field
    if ((value && value.trim() !== "") || minPrice !== "" || maxPrice !== "") {
      let timeoutId = setTimeout(() => {}, 500);
      timeoutId = setTimeout(() => fuzzySearch(), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [value, minPrice, maxPrice]);

  /**
   * Simple spell correction algorithm using Levenshtein distance
   * You can enhance this with a more sophisticated spell checker
   */
  const autoCorrect = (word, dictionary) => {
    if (!word || !dictionary || dictionary.length === 0) return word;

    let minDistance = Infinity;
    let closestMatch = word;

    dictionary.forEach((dictWord) => {
      const distance = levenshteinDistance(
        word.toLowerCase(),
        dictWord.toLowerCase()
      );
      if (distance < minDistance && distance <= 2) {
        // Allow max 2 character differences
        minDistance = distance;
        closestMatch = dictWord;
      }
    });

    return minDistance <= 2 ? closestMatch : word;
  };

  /**
   * Calculate Levenshtein distance between two strings
   */
  const levenshteinDistance = (str1, str2) => {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1 // deletion
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  };

const fuzzySearch = async () => {
    console.log("Search ProductId Length", props?.totalRcordId?.length);
    
    // ✅ SAFETY CHECK: Don't call API if search value is empty or whitespace
    if (!value || value.trim() === '') {
        console.log("fuzzySearch: Skipping API call - empty search value");
        setFuzzySearches([]);
        setResultCount(0);
        setCorrectedQuery("");
        return;
    }

    // ✅ FOR AUTOCOMPLETE DROPDOWN: Use fuzzy search (Atlas Search)
    let param = "";
    let filed = "";
    
    if (searchType == "sku") {
        // Use fuzzy matching for autocomplete
        param = { "SKU-partial": value.trim() };
        filed = { productName: 1, SKU: 1 };
    } else {
        // Use fuzzy matching for autocomplete
        param = { "productName-fuzzy": value.trim() };
        filed = { productName: 1, SKU: 1 };
    }

    // Build price filter for otherFilter
    const priceFilter = {};
    if (minPrice) {
        priceFilter.$gte = parseFloat(minPrice);
    }
    if (maxPrice) {
        priceFilter.$lte = parseFloat(maxPrice);
    }

    // Merge price filter into otherFilter
    const otherFilterWithPrice = { ...props.searchParam };
    if (Object.keys(priceFilter).length > 0) {
        otherFilterWithPrice.price = priceFilter;
    }

    let paramData = {
        filter: { ...param },
        otherFilter: otherFilterWithPrice,
        excludeField: { ...filed }
    };

    paramData["productId"] = props?.totalRcordId ? props.totalRcordId : [];
    
    try {
        const data =
            props.apiurl === "OtherSellerProducts"
                ? await api.product.searchOtherSellerProducts(paramData)
                : await api.product.searchSellerProductsFilter(paramData);

        console.log("fuzzySearch ", skipFuzzySearch.current);
        
        if (!skipFuzzySearch.current) {
            // Auto-correction logic
            if (data.products && data.products.length > 0) {
                const dictionary = data.products.map((p) =>
                    searchType === "sku" ? p.SKU : p.productName
                );
                const corrected = autoCorrect(value, dictionary);
                if (corrected.toLowerCase() !== value.toLowerCase()) {
                    setCorrectedQuery(corrected);
                } else {
                    setCorrectedQuery("");
                }
            }
            
            setFuzzySearches(data.products);
            // Don't set count here - it will be set after final search
            // setResultCount(data.products.length);
        } else {
            setFuzzySearches([]);
            setCorrectedQuery("");
        }
    } catch (error) {
        console.error("Fuzzy search failed:", error);
        setFuzzySearches([]);
        setResultCount(0);
    }
};

const handleEnterPress = async () => {
    console.log("handleEnterPress");
    
    // ✅ SAFETY CHECK: Don't proceed if search value is empty
    if (!value || !value.trim()) {
        console.log("handleEnterPress: Skipping - empty search value");
        return;
    }
    
    if (props?.searchByKeyword && value.trim()) {
        try {
            skipFuzzySearch.current = true;
            
            // ✅ FOR FINAL SEARCH: Use partial matching
            const param =
                searchType === "sku"
                    ? { "SKU-partial": value.trim() }
                    : { "productName-fuzzy": value.trim() };

            // Build price filter for otherFilter
            const priceFilter = {};
            if (minPrice) {
                priceFilter.$gte = parseFloat(minPrice);
            }
            if (maxPrice) {
                priceFilter.$lte = parseFloat(maxPrice);
            }

            // Merge price filter into otherFilter
            const otherFilterWithPrice = { ...props.searchParam };
            if (Object.keys(priceFilter).length > 0) {
                otherFilterWithPrice.price = priceFilter;
            }

            const paramData = {
                filter: { ...param },
                otherFilter: otherFilterWithPrice,
                excludeField: { productName: 1, SKU: 1 },
                productId: props?.totalRcordId || []
            };

            const data =
                props.apiurl === "OtherSellerProducts"
                    ? await api.product.searchOtherSellerProducts(paramData)
                    : await api.product.searchSellerProductsFilter(paramData);
            
            const productIds = data.products.map((item) => item._id);
            
            // Pass productIds to parent and let parent call productsRetrieveall
            // Parent will then call our onCountUpdate callback with the actual count
            if (productIds.length > 0) {
                props.searchByKeyword(value, searchType, productIds);
            } else {
                // If no results, show count immediately
                setResultCount(0);
                setShowResultCount(true);
                props.searchByKeyword(value, searchType, []);
            }
            
            setFuzzySearches([]);
        } catch (error) {
            console.error("Enter search failed", error);
            setResultCount(0);
            setShowResultCount(true);
        } finally {
            setTimeout(() => {
                skipFuzzySearch.current = false;
                console.log("setTimeout ", skipFuzzySearch.current);
            }, 1000);
        }
    }
};

  const handleSearchClick = async () => {
    console.log("handleSearchClick");
    
    // ✅ SAFETY CHECK: Don't proceed if search value is empty
    if (!value || !value.trim()) {
        console.log("handleSearchClick: Skipping - empty search value");
        return;
    }
    
    if (props?.searchByKeyword && value.trim()) {
        handleEnterPress();
        return;
    }
    try {
        skipFuzzySearch.current = true;
        
        // ✅ FOR FINAL SEARCH: Use partial matching
        const param =
            searchType === "sku"
                ? { "SKU-partial": value.trim() }
                : { "productName-fuzzy": value.trim() };

        // Build price filter for otherFilter
        const priceFilter = {};
        if (minPrice) {
            priceFilter.$gte = parseFloat(minPrice);
        }
        if (maxPrice) {
            priceFilter.$lte = parseFloat(maxPrice);
        }

        // Merge price filter into otherFilter
        const otherFilterWithPrice = { ...props.searchParam };
        if (Object.keys(priceFilter).length > 0) {
            otherFilterWithPrice.price = priceFilter;
        }

        const paramData = {
            filter: { ...param },
            otherFilter: otherFilterWithPrice,
            excludeField: { productName: 1, SKU: 1 },
            productId: props?.totalRcordId || []
        };

        const data =
            props.apiurl === "OtherSellerProducts"
                ? await api.product.searchOtherSellerProducts(paramData)
                : await api.product.searchSellerProductsFilter(paramData);
        
        if (props?.searchByKeyword) {
            const productIds = data.products.map((item) => item._id);
            // Don't set count here - let parent do it after productsRetrieveall
            // setResultCount(productIds.length);
            // setShowResultCount(true);
            props.searchByKeyword(value, searchType, productIds);
        }
        setFuzzySearches([]);
    } catch (error) {
        console.error("Search button click failed", error);
        setResultCount(0);
        setShowResultCount(true);
    } finally {
        setTimeout(() => {
            skipFuzzySearch.current = false;
        }, 1000);
    }
};


  const handleClearAll = () => {
    setValue("");
    setMinPrice("");
    setMaxPrice("");
    setHsnFilter("All");
    setFuzzySearches([]);
    setResultCount(0);
    setShowResultCount(false);
    setCorrectedQuery("");
    if (props?.onHSNFilterChange) {
      props.onHSNFilterChange(null);
    }
    if (props?.apiCall) {
      props.apiCall();
    }
  };

  const handleHSNFilterChange = (e) => {
    const selectedFilter = e.target.value;
    setHsnFilter(selectedFilter);

    if (selectedFilter === "Products without HSN") {
      props.onHSNFilterChange({ HSN: { $exists: false } });
    } else if (selectedFilter === "Products with HSN") {
      props.onHSNFilterChange({
        $and: [{ HSN: { $exists: true } }, { HSN: { $ne: "" } }],
      });
    } else {
      props.onHSNFilterChange(null);
    }
  };

  // Expose method to update count from parent
  useEffect(() => {
    if (props.onCountUpdate) {
      // Store the function so parent can call it
      props.onCountUpdate((count) => {
        setResultCount(count);
        setShowResultCount(true);
      });
    }
  }, [props.onCountUpdate]);

  return (
    <div className="product-search-container">
      <div className="filter-row">
        {/* Search Type Dropdown */}
        <div className="filter-item">
          <select
            className="filter-select search-type-select"
            value={searchType}
            onChange={(e) => {
              setValue("");
              setSearchType(e.target.value);
              setFuzzySearches([]);
              setResultCount(0);
              setShowResultCount(false);
              setCorrectedQuery("");
            }}
          >
            <option value="Product Name">Product Name</option>
            <option value="sku">SKU</option>
            { (props?.from !== "orders" && props?.from !== "priceReq") && <option value="HSN">HSN</option>}
          </select>
        </div>

        {/* Conditional Input: Text box for Product Name/SKU or Dropdown for HSN */}
        {searchType === "HSN" && props?.from !== "orders" ? (
          <div className="filter-item">
            <select
              className="filter-select hsn-filter-select"
              value={hsnFilter}
              onChange={handleHSNFilterChange}
            >
              <option value="All">All HSN</option>
              <option value="Products without HSN">Products without HSN</option>
              <option value="Products with HSN">Products with HSN</option>
            </select>
          </div>
        ) : (
          <div className="filter-item search-input-container" ref={searchContainerRef}>
            <input
              type="text"
              className="filter-input search-input"
              placeholder={`Search by ${searchType === "sku" ? "SKU" : "Product Name"}`}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleEnterPress();
                }
              }}
            />
            {fuzzySearches && fuzzySearches.length > 0 && (
              <div className="fuzzy-search-dropdown">
                <div className="dropdown-header">
                  {fuzzySearches.length} result{fuzzySearches.length !== 1 ? 's' : ''} found
                </div>
                {fuzzySearches.map((product) => (
                  <div
                    key={product._id}
                    className="fuzzy-search-item"
                    onClick={() => {
                      setnewEnqName(
                        props.from == "orders"
                          ? product._id
                          : searchType == "sku"
                          ? product.SKU
                          : product.productName
                      );
                      setFuzzySearches(null);
                      setShowResultCount(false);
                      props.filterSearch({ data: product._id, type: searchType });
                    }}
                  >
                    {searchType == "sku" ? product.SKU : product.productName}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Search Button */}
        <button className="filter-btn search-btn" onClick={handleSearchClick} title="Search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        </button>

        {/* Clear Button */}
        <button className="filter-btn clear-btn" onClick={handleClearAll} title="Clear All Filters">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span className="clear-text">Clear</span>
        </button>
        
      {/* Result Count Display */}
      {showResultCount && (
        <div className="result-count-display">
          <span className="result-count-text">
            {resultCount > 0 
              ? `${resultCount} result${resultCount !== 1 ? 's' : ''} found`
              : 'No results found'
            }
          </span>
        </div>
      )}
      </div>

    </div>
  );
}

export default ProductSKUSearch;